import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { guestApiClient } from '../../services/apiClient';
import { formatCurrency } from '../../utils/currency';
import { BookingDetails } from '../../types';
import { GDTop, GDDone, GDSteps, GDSpinnerScreen } from './GDBits';

interface CheckoutScreenProps {
  booking: BookingDetails;
}

interface CheckoutInfo {
  enabled: boolean;
  reservation_status: string;
  balance: number;
  due_from_guest: number;
  currency: string;
  instructions: string;
  photo_uploaded: boolean;
  can_checkout: boolean;
}

type Step =
  | 'loading'
  | 'payment'
  | 'instructions'
  | 'photo'
  | 'done'
  // Wrong-state landings (a scanned check-out QR on a booking that has
  // already left, or hasn't arrived yet) explain themselves instead of
  // silently bouncing the guest to the home screen.
  | 'already_out'
  | 'not_checked_in'
  | 'unavailable';

/**
 * Guest self check-out: pending payment gate → hotel instructions →
 * room photo upload → the check-out button. Server enforces the
 * balance gate again on completion (409 → back to the payment step).
 */
const CheckoutScreen: React.FC<CheckoutScreenProps> = ({ booking }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [info, setInfo] = useState<CheckoutInfo | null>(null);
  const [step, setStep] = useState<Step>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoDone, setPhotoDone] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const res: any = await guestApiClient.get('/api/guest/self-checkout/');
      const data: CheckoutInfo = res?.data?.data || res?.data;
      setInfo(data);
      setPhotoDone(!!data.photo_uploaded);
      if (!data.enabled) setStep('unavailable');
      else if (data.reservation_status === 'checked_out') setStep('already_out');
      else if (data.reservation_status !== 'checked_in') setStep('not_checked_in');
      else if (data.due_from_guest > 0.01) setStep('payment');
      else setStep('instructions');
    } catch {
      setError(t('gd.checkout.loadError', 'Could not load check-out details. Please try again.'));
      setStep('unavailable');
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const uploadPhoto = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      await guestApiClient.uploadFile('/api/guest/self-checkout/photo/', file, {}, 'photo');
      setPhotoDone(true);
    } catch (e: any) {
      setError(e?.error || t('gd.checkout.photoError', 'Photo upload failed — please try again.'));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const completeCheckout = async () => {
    setBusy(true);
    setError(null);
    try {
      await guestApiClient.post('/api/guest/self-checkout/complete/', {});
      queryClient.invalidateQueries();
      setStep('done');
    } catch (e: any) {
      const body = e?.body || {};
      if (body.error_code === 'balance_due') {
        setInfo((prev) => prev ? { ...prev, due_from_guest: body.due_from_guest } : prev);
        setStep('payment');
      } else {
        setError(body.error || e?.error || t('gd.checkout.completeError', 'Check-out failed — please contact reception.'));
      }
    } finally {
      setBusy(false);
    }
  };

  if (step === 'loading' || !info) {
    return <GDSpinnerScreen label={t('gd.common.loading')} />;
  }

  const stepIndex = step === 'payment' ? 1 : step === 'instructions' ? 2 : 3;
  // Steps that are a standalone message, not part of the 3-step flow.
  const isNotice = ['done', 'unavailable', 'already_out', 'not_checked_in'].includes(step);

  return (
    <section className="gd-screen">
      <GDTop title={t('gd.checkout.title', 'Check out')} onBack={() => navigate('/home')} />
      <div className="gd-body">
        {!isNotice && <GDSteps total={3} current={stepIndex} />}

        {error && <div className="gd-note" role="alert">{error}</div>}

        {step === 'unavailable' && !error && (
          <div className="gd-note">
            {t('gd.checkout.unavailable', 'Self check-out is not available — please contact reception.')}
          </div>
        )}

        {step === 'already_out' && (
          <GDDone tone="orange" icon="👋" title={t('gd.checkout.alreadyOutTitle', 'You have already checked out')}>
            <p>
              {t('gd.checkout.alreadyOutBody', 'This booking is already checked out. If you need anything else, please contact reception.')}
            </p>
            <button type="button" className="gd-btn gd-ghost" onClick={() => navigate('/home')}>
              {t('gd.checkout.backHome', 'Back to home')}
            </button>
          </GDDone>
        )}

        {step === 'not_checked_in' && (
          <GDDone tone="orange" icon="🪪" title={t('gd.checkout.notCheckedInTitle', 'You are not checked in yet')}>
            <p>
              {t('gd.checkout.notCheckedInBody', 'Check-out becomes available once you have checked in. Start with check-in first.')}
            </p>
            <button type="button" className="gd-btn" onClick={() => navigate('/checkin')}>
              {t('gd.checkout.goCheckin', 'Go to check-in')}
            </button>
            <button type="button" className="gd-btn gd-ghost" onClick={() => navigate('/home')}>
              {t('gd.checkout.backHome', 'Back to home')}
            </button>
          </GDDone>
        )}

        {step === 'payment' && (
          <>
            <div className="gd-note" role="alert">
              <b>{t('gd.checkout.pendingTitle', 'Pending payment')}</b>
              <p style={{ margin: '6px 0 0' }}>
                {t('gd.checkout.pendingBody', 'Please settle your outstanding balance before checking out.')}
              </p>
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 14px', border: '1px solid var(--gd-line, #e5e7eb)',
              borderRadius: 12, fontSize: 15,
            }}>
              <span>{t('gd.checkout.amountDue', 'Amount due')}</span>
              <b>{formatCurrency(info.due_from_guest, info.currency)}</b>
            </div>
            <button type="button" className="gd-btn" disabled={busy} onClick={() => navigate('/pay?for=checkout')}>
              {t('gd.checkout.payNow', 'Pay now')}
            </button>
            <button type="button" className="gd-btn gd-ghost" disabled={busy} onClick={load}>
              {t('gd.checkout.refresh', 'I have paid — refresh')}
            </button>
          </>
        )}

        {step === 'instructions' && (
          <>
            <h3 style={{ margin: 0 }}>{t('gd.checkout.instructionsTitle', 'Before you go')}</h3>
            <p style={{ whiteSpace: 'pre-line', fontSize: 14, lineHeight: 1.6, color: 'var(--gd-ink-2)', margin: 0 }}>
              {info.instructions}
            </p>
            <button type="button" className="gd-btn" onClick={() => setStep('photo')}>
              {t('gd.checkout.continue', 'Continue')}
            </button>
          </>
        )}

        {step === 'photo' && (
          <>
            <h3 style={{ margin: 0 }}>{t('gd.checkout.photoTitle', 'One last thing')}</h3>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--gd-ink-2)', margin: 0 }}>
              {t('gd.checkout.photoBody', 'Please take a quick photo of your room as you leave it.')}
            </p>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
            />
            <button
              type="button"
              className={`gd-btn ${photoDone ? 'gd-ghost' : ''}`}
              disabled={busy}
              onClick={() => fileInput.current?.click()}
            >
              {busy
                ? t('gd.checkout.uploading', 'Uploading…')
                : photoDone
                  ? t('gd.checkout.retakePhoto', '✓ Photo uploaded — retake')
                  : t('gd.checkout.takePhoto', 'Take / upload photo')}
            </button>
            <button
              type="button"
              className="gd-btn"
              disabled={busy || !photoDone}
              onClick={completeCheckout}
            >
              {busy ? t('gd.checkout.working', 'Checking out…') : t('gd.checkout.completeBtn', 'Complete check-out')}
            </button>
          </>
        )}

        {step === 'done' && (
          <GDDone title={t('gd.checkout.doneTitle', 'Checked out — thank you!')}>
            <p>
              {t('gd.checkout.doneBody', 'Your check-out is complete. We hope to welcome you again soon — safe travels!')}
            </p>
            <button type="button" className="gd-btn gd-ghost" onClick={() => navigate('/home')}>
              {t('gd.checkout.backHome', 'Back to home')}
            </button>
          </GDDone>
        )}
      </div>
    </section>
  );
};

export default CheckoutScreen;
